import { client } from './sanity'
import { getOrCreateSessionId } from './session'

export interface LoginCredentials {
  email: string
  password: string
}

export interface AuthResponse {
  user: {
    _id: string
    email: string
    name?: string
    role?: string
  }
  token?: string
}

/**
 * Authenticate user with email and password
 * 
 * NOTE: Sanity.io doesn't provide a public auth API for custom apps.
 * This implementation uses Sanity user documents for authentication.
 * 
 * For production, consider:
 * 1. Using a proper auth service (Auth0, Firebase, Clerk)
 * 2. Implementing password hashing (bcrypt, argon2)
 * 3. Adding rate limiting and security measures
 */
export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  const { email, password } = credentials
  
  try {
    // Fetch user from Sanity
    // NOTE: In production, passwords should be hashed (bcrypt, argon2)
    // This is a demo implementation
    const user = await client.fetch<{
      _id: string
      email: string
      name?: string
      role?: string
      passwordHash?: string // In production, store hashed passwords only
      // For demo: you might store plain password temporarily
      password?: string // Remove in production!
    }>(`
      *[_type == "user" && email == $email][0] {
        _id,
        email,
        name,
        role,
        passwordHash,
        password
      }
    `, { email })
    
    if (!user) {
      throw new Error('Invalid email or password')
    }
    
    // TODO: Verify password hash using bcrypt or similar
    // In production: await bcrypt.compare(password, user.passwordHash)
    // For demo: simple comparison (REMOVE IN PRODUCTION!)
    if (user.password && user.password !== password) {
      throw new Error('Invalid email or password')
    }
    
    // If using passwordHash, verify it:
    // if (user.passwordHash && !await bcrypt.compare(password, user.passwordHash)) {
    //   throw new Error('Invalid email or password')
    // }
    
    // Get or create unique session ID for this browser
    const sessionId = getOrCreateSessionId()
    
    // Create session in Sanity with unique session ID
    // Clear any existing session for this browser first
    try {
      await client.delete(sessionId)
    } catch {
      // Ignore if no session exists
    }
    
    // Create new session with unique ID
    await client.create({
      _id: sessionId,
      _type: 'session',
      user: { _ref: user._id },
      createdAt: new Date().toISOString(),
    })
    
    return {
      user: {
        _id: user._id,
        email: user.email,
        name: user.name,
        role: user.role,
      },
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error
    }
    throw new Error('Authentication failed')
  }
}

/**
 * Get current authenticated user from session
 */
export async function getCurrentUser(): Promise<AuthResponse['user'] | null> {
  try {
    // Get session ID from cookie
    const { getSessionId } = await import('./session')
    const sessionId = getSessionId()
    
    if (!sessionId) {
      return null
    }
    
    // Check for active session in Sanity using unique session ID
    const session = await client.fetch<{
      user?: {
        _id: string
        email: string
        name?: string
        role?: string
      }
    }>(`
      *[_type == "session" && _id == $sessionId && defined(user)][0] {
        user-> {
          _id,
          email,
          name,
          role
        }
      }
    `, { sessionId })
    
    if (!session?.user) {
      return null
    }
    
    return session.user
  } catch (error) {
    console.error('Error getting current user:', error)
    return null
  }
}

/**
 * Logout current user by clearing session
 */
export async function logout(): Promise<void> {
  try {
    // Get session ID from cookie
    const { getSessionId, clearSessionId } = await import('./session')
    const sessionId = getSessionId()
    
    if (sessionId) {
      // Delete session from Sanity
      try {
        await client.delete(sessionId)
      } catch {
        // Ignore if session doesn't exist
      }
    }
    
    // Clear session ID cookie
    clearSessionId()
  } catch (error) {
    console.error('Error logging out:', error)
    // Don't throw - allow logout even if Sanity delete fails
    // Still clear the cookie
    const { clearSessionId } = await import('./session')
    clearSessionId()
  }
}
